import { ElementType, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { DataGridProps, GridColDef, GridRowsProp } from '@mui/x-data-grid'

import TablePageContainer from 'shared/components/TablePageContainer'
import { ApolloError } from '@apollo/client'
import { StyledDataGridWithMargin } from './PaginatedTable.style'

interface PaginatedTableProps {
  title: string
  rows: GridRowsProp
  columns: GridColDef[]
  page: number
  pageSize: number
  rowCount: number
  loading: boolean
  error?: ApolloError
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  localeText?: DataGridProps['localeText']
  rowsPerPageOptions?: number[]
  GridComponent?: ElementType
  gridProps?: Record<string, unknown>
  children?: ReactNode
  errorMessage?: string
}

const PaginatedTable = ({
  title,
  rows,
  columns,
  page,
  pageSize,
  rowCount,
  loading,
  error,
  onPageChange,
  onPageSizeChange,
  rowsPerPageOptions = [10, 20, 50],
  GridComponent = StyledDataGridWithMargin,
  gridProps,
  children,
  localeText,
  errorMessage,
}: PaginatedTableProps) => {
  const { t } = useTranslation()

  const defaultLocaleText: DataGridProps['localeText'] = {
    noRowsLabel: t('noResults'),
    noResultsOverlayLabel: t('noResults'),
    MuiTablePagination: {
      labelRowsPerPage: t('rowsPerPage'),
      labelDisplayedRows: ({ from, to, count }) =>
        t('displayedRows', { from, to, count }),
    },
  }

  if (error) {
    return (
      <TablePageContainer title={title}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: 32,
            color: 'red',
          }}
        >
          {errorMessage}
        </div>
      </TablePageContainer>
    )
  }

  return (
    <TablePageContainer title={title}>
      <GridComponent
        {...gridProps}
        rows={rows}
        columns={columns}
        page={page}
        pageSize={pageSize}
        pagination
        paginationMode="server"
        rowCount={rowCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        rowsPerPageOptions={rowsPerPageOptions}
        localeText={localeText ?? defaultLocaleText}
        disableSelectionOnClick
        disableColumnMenu
        loading={loading}
      />

      {children}
    </TablePageContainer>
  )
}

export default PaginatedTable
